import React, { useState, useEffect } from 'react';
import { Store, Save, Key, Globe, AlertCircle, CheckCircle2 } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { apiRequest } from '../../../utils/api-request';

interface ShopifyProfile {
  shop_url: string;
  access_token: string;
  api_version: string;
}

const ShopifyProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<ShopifyProfile>({
    shop_url: '',
    access_token: '',
    api_version: '2024-01'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Load profile from API
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest('/shopify/profile', { method: 'GET' });
      if (response.success && response.data) {
        setProfile({
          shop_url: response.data.shop_url || '',
          access_token: response.data.access_token || '',
          api_version: response.data.api_version || '2024-01'
        });
      }
    } catch (error) {
      console.error('Failed to load Shopify profile:', error);
      setMessage({ type: 'error', text: 'Failed to load Shopify profile.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await apiRequest('/shopify/profile', {
        method: 'POST',
        body: profile
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Shopify profile saved successfully!' });
        // Clear message after 3 seconds
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to save Shopify profile.' });
      }
    } catch (error) {
      console.error('Failed to save Shopify profile:', error);
      setMessage({ type: 'error', text: 'Failed to save Shopify profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof ShopifyProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="sticky top-0 z-10">
        <ModernSettingHeader
          title="Shopify Profile"
          description="Configure your Shopify store connection"
          icon={<Store className="w-6 h-6" />}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">

        {/* Message Alert */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 space-y-6">
            {/* Shop URL */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Globe className="h-4 w-4" />
                <span>Shop URL</span>
              </label>
              <input
                type="text"
                value={profile.shop_url}
                onChange={(e) => handleInputChange('shop_url', e.target.value)}
                placeholder="your-store.myshopify.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter your Shopify store URL (e.g., your-store.myshopify.com)
              </p>
            </div>

            {/* Access Token */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Key className="h-4 w-4" />
                <span>Access Token</span>
              </label>
              <input
                type="password"
                value={profile.access_token}
                onChange={(e) => handleInputChange('access_token', e.target.value)}
                placeholder="shpat_xxxxxxxxxxxxx"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter your Shopify Admin API access token
              </p>
            </div>

            {/* API Version */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <span>API Version</span>
              </label>
              <select
                value={profile.api_version}
                onChange={(e) => handleInputChange('api_version', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="2024-01">2024-01</option>
                <option value="2023-10">2023-10</option>
                <option value="2023-07">2023-07</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select the Shopify API version to use
              </p>
            </div>
          </div>

          {/* Footer with Save Button */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving || !profile.shop_url || !profile.access_token}
              className="flex items-center space-x-2 px-6 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Save className="h-4 w-4" />
              <span>{isSaving ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">How to get your Shopify credentials:</h3>
          <ol className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-decimal list-inside">
            <li>Log in to your Shopify Admin panel</li>
            <li>Navigate to Settings → Apps and sales channels → Develop apps</li>
            <li>Create a new app or use an existing one</li>
            <li>Install the app and copy the Admin API access token</li>
            <li>Make sure to grant the necessary permissions for products, orders, and customers</li>
          </ol>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ShopifyProfilePage;
